/**
 * Price Calculator Utilities
 *
 * Functions for calculating prices based on floors and selected options.
 */

import {
  CREDIT_TO_EUR,
  MeasurementServiceData,
  measurementServicePricing,
  MeasurementServicePricingConfig,
  PricingConfig,
} from "./config";

export interface PriceCalculation {
  subtotal: number; // Price before VAT in EUR
  vat: number; // VAT amount in EUR
  total: number; // Total price including VAT in EUR
  credits: number; // Total in credits
}

/**
 * Calculate total price based on floors and selected options
 *
 * @param config - Pricing configuration for the service
 * @param floors - Number of floors
 * @param selectedOptions - Array of selected option names
 * @returns Price calculation breakdown
 */
export function calculatePrice(
  config: PricingConfig,
  floors: number,
  selectedOptions: string[],
): PriceCalculation {
  // Start with base price
  let totalCredits = config.basePrice;

  // Add price per floor (multiply by total number of floors)
  totalCredits += config.pricePerFloor * floors;

  // Add option prices
  selectedOptions.forEach((optionName) => {
    const option = config.options[optionName];
    if (option) {
      if (option.pricePerFloor) {
        totalCredits += option.pricePerFloor * floors;
      }
      if (option.pricePerOrder) {
        totalCredits += option.pricePerOrder;
      }
    }
  });

  // Convert credits to EUR
  const totalEUR = totalCredits * CREDIT_TO_EUR;

  // Calculate VAT (price already includes VAT, so we extract it)
  // Total = Subtotal * 1.19
  // Subtotal = Total / 1.19
  // VAT = Total - Subtotal
  const subtotal = totalEUR / 1.19;
  const vat = totalEUR - subtotal;

  return {
    subtotal,
    vat,
    total: totalEUR,
    credits: totalCredits,
  };
}

/**
 * Format a EUR amount in German locale (e.g. "1.400,39 EUR").
 *
 * Uses Intl.NumberFormat with style "currency" so thousand/decimal separators
 * follow de-DE rules. We pick currencyDisplay "code" so the suffix is the
 * literal "EUR" string instead of the "€" glyph -- this is the format
 * requested in ticket #30.
 */
export function formatEUR(amount: number): string {
  // Intl.NumberFormat inserts a non-breaking space between the number and
  // the currency code; that's fine for layout but we keep the helper simple.
  return new Intl.NumberFormat("de-DE", {
    style: "currency",
    currency: "EUR",
    currencyDisplay: "code",
  }).format(amount);
}

/**
 * Format price for German locale, without a currency suffix.
 *
 * @param price - Price in EUR
 * @returns Formatted price string (e.g., "1.400,39")
 */
export function formatPrice(price: number): string {
  return new Intl.NumberFormat("de-DE", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(price);
}

/**
 * Format price with currency suffix (German locale).
 *
 * @param price - Price in EUR
 * @returns Formatted price string with currency (e.g., "1.400,39 EUR")
 */
export function formatPriceWithCurrency(price: number): string {
  return formatEUR(price);
}

/**
 * Convert a gross price (including 19% VAT) to a net price (excluding VAT).
 *
 * @param grossPrice - Price including 19% VAT
 * @returns Net price rounded to 2 decimal places
 */
export function grossToNet(grossPrice: number): number {
  return Math.round((grossPrice / 1.19) * 100) / 100;
}

/**
 * Format a price for display depending on the chosen price display mode.
 * When mode is "net", the gross price is converted to net before formatting.
 * When mode is "gross" (default), the price is returned as-is.
 *
 * @param grossPrice - Price including 19% VAT
 * @param priceDisplay - "gross" or "net"
 * @returns Formatted price string with currency (e.g., "19,00 €")
 */
export function formatDisplayPrice(
  grossPrice: number,
  priceDisplay: "gross" | "net" = "gross",
): string {
  const price = priceDisplay === "net" ? grossToNet(grossPrice) : grossPrice;
  return formatPriceWithCurrency(price);
}

/**
 * Calculate measurement service price based on all parameters
 *
 * @param data - Measurement service data including property type, floors, etc.
 * @returns Price calculation breakdown
 */
export function calculateMeasurementServicePrice(
  data: MeasurementServiceData,
  config: MeasurementServicePricingConfig = measurementServicePricing,
): PriceCalculation {
  let totalCredits = config.basePrice;

  const floors = Math.max(1, Math.trunc(data.floors || 1));
  const squareMeter = Math.max(0, Math.trunc(data.squareMeter || 0));
  const livingUnits = Math.max(1, Math.trunc(data.livingUnits || 1));
  const isCommercial = data.propertyType === "multiFamilyHouse";

  totalCredits += Math.max(0, floors - 1) * config.levelPriceAfterFirstLevel;

  totalCredits +=
    Math.max(0, squareMeter - config.includedSquareMeters) *
    config.additionalSquareMeterPrice;

  if (isCommercial) {
    totalCredits += Math.max(0, livingUnits - 1) * config.flatNumberPrice;
  }

  if (data.additionalLivingSpaceCalculation) {
    totalCredits +=
      floors *
      (isCommercial
        ? config.additionalLivingSpaceCalculationCommercial
        : config.additionalLivingSpaceCalculationResidential);
  }

  if (data.cutNeeded) {
    totalCredits +=
      floors * (isCommercial ? config.cutCommercial : config.cutResidential);
  }

  if (data.dwgFile) {
    totalCredits += floors * config.dwgFile;
  }

  const extraViewsCount =
    {
      oneView: 1,
      twoViews: 2,
      threeViews: 3,
      fourViews: 4,
    }[data.extraViews] ?? 0;
  totalCredits +=
    extraViewsCount *
    (isCommercial ? config.viewsCommercial : config.viewsResidential);

  if (data.matterport === "forThreeMonth") {
    totalCredits += floors * config.matterportThreeMonths;
  }
  if (data.matterport === "forSixMonth") {
    totalCredits += floors * config.matterportSixMonths;
  }

  // Convert credits to EUR
  const totalEUR = totalCredits * CREDIT_TO_EUR;

  // Calculate VAT (price already includes VAT, so we extract it)
  const subtotal = totalEUR / 1.19;
  const vat = totalEUR - subtotal;

  return {
    subtotal,
    vat,
    total: totalEUR,
    credits: totalCredits,
  };
}
